import { logger } from '@/shared/utils/logger';

/**
 * Cookie Consent Service
 * Manages user cookie preferences with GDPR/CCPA compliance
 * Following 2025 best practices for consent management
 */

export interface CookieCategories {
  essential: boolean; // Always true, cannot be disabled
  analytics: boolean;
  functional: boolean;
  marketing: boolean;
}

export interface ConsentData {
  version: string;
  timestamp: string;
  expiresAt: string;
  categories: CookieCategories;
  gpcSignal?: boolean; // Global Privacy Control for CCPA
}

const CONSENT_KEY = 'donvio_cookie_consent';
const CONSENT_VERSION = '2.0'; // Increment to force re-consent
const CONSENT_DURATION_MONTHS = 12; // GDPR standard: 12 months

/**
 * Cookie category descriptions for transparency
 */
export const COOKIE_DESCRIPTIONS = {
  essential: {
    title: 'Essential Cookies',
    description: 'Required for the website to function properly. These cannot be disabled.',
    purpose: 'Session management, security, authentication, and core functionality',
    examples: ['Authentication tokens', 'Session identifiers', 'Security tokens'],
    duration: 'Session or as needed for functionality',
  },
  analytics: {
    title: 'Analytics & Performance',
    description: 'Help us understand how visitors interact with our website.',
    purpose: 'Error tracking, performance monitoring, and usage analytics',
    examples: ['Sentry error tracking', 'Performance metrics'],
    duration: 'Up to 12 months',
  },
  functional: {
    title: 'Functional Cookies',
    description: 'Enable enhanced functionality and personalization.',
    purpose: 'Remember your preferences and settings',
    examples: ['Language preferences', 'UI customizations'],
    duration: 'Up to 12 months',
  },
  marketing: {
    title: 'Marketing & Advertising',
    description: 'Used to track visitors across websites for marketing purposes.',
    purpose: 'Targeted advertising and conversion tracking',
    examples: ['Ad tracking pixels', 'Remarketing tags'],
    duration: 'Up to 12 months',
  },
} as const;

class CookieConsentService {
  /**
   * Get current consent data from storage
   */
  getConsent(): ConsentData | null {
    try {
      const stored = localStorage.getItem(CONSENT_KEY);
      if (!stored) return null;

      const consent: ConsentData = JSON.parse(stored);

      // Check if consent has expired
      if (this.isConsentExpired(consent)) {
        logger.info('Cookie consent has expired');
        this.clearConsent();
        return null;
      }

      // Check if version has changed
      if (consent.version !== CONSENT_VERSION) {
        logger.info('Cookie consent version outdated');
        this.clearConsent();
        return null;
      }

      return consent;
    } catch (error) {
      logger.error('Error reading cookie consent:', error);
      return null;
    }
  }

  /**
   * Check if a specific category is consented
   */
  hasConsent(category: keyof CookieCategories): boolean {
    const consent = this.getConsent();
    if (!consent) return false;
    return consent.categories[category] === true;
  }

  /**
   * Check if consent has expired
   */
  private isConsentExpired(consent: ConsentData): boolean {
    try {
      const expiresAt = new Date(consent.expiresAt);
      return Date.now() > expiresAt.getTime();
    } catch {
      return true;
    }
  }

  /**
   * Calculate expiration date
   */
  private getExpirationDate(): string {
    const now = new Date();
    now.setMonth(now.getMonth() + CONSENT_DURATION_MONTHS);
    return now.toISOString();
  }

  /**
   * Save consent preferences
   */
  saveConsent(categories: CookieCategories, gpcSignal?: boolean): ConsentData {
    const consent: ConsentData = {
      version: CONSENT_VERSION,
      timestamp: new Date().toISOString(),
      expiresAt: this.getExpirationDate(),
      categories: {
        essential: true, // Always true
        analytics: categories.analytics,
        functional: categories.functional,
        marketing: categories.marketing,
      },
      gpcSignal,
    };

    try {
      localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
      logger.info('Cookie consent saved:', consent);
    } catch (error) {
      logger.error('Error saving cookie consent:', error);
    }

    return consent;
  }

  /**
   * Accept all cookies
   */
  acceptAll(gpcSignal?: boolean): ConsentData {
    return this.saveConsent(
      {
        essential: true,
        analytics: true,
        functional: true,
        marketing: true,
      },
      gpcSignal
    );
  }

  /**
   * Reject all non-essential cookies
   */
  rejectAll(gpcSignal?: boolean): ConsentData {
    return this.saveConsent(
      {
        essential: true,
        analytics: false,
        functional: false,
        marketing: false,
      },
      gpcSignal
    );
  }

  /**
   * Accept only essential cookies (same as reject all)
   */
  essentialOnly(gpcSignal?: boolean): ConsentData {
    return this.rejectAll(gpcSignal);
  }

  /**
   * Clear consent data
   */
  clearConsent(): void {
    try {
      localStorage.removeItem(CONSENT_KEY);
      logger.info('Cookie consent cleared');
    } catch (error) {
      logger.error('Error clearing cookie consent:', error);
    }
  }

  /**
   * Check if user needs to be prompted for consent
   */
  needsConsent(): boolean {
    return this.getConsent() === null;
  }

  /**
   * Detect Global Privacy Control signal (CCPA)
   */
  detectGPC(): boolean {
    return (navigator as unknown as { globalPrivacyControl?: boolean }).globalPrivacyControl ?? false;
  }

  /**
   * Get days until consent expires
   */
  getDaysUntilExpiration(): number | null {
    const consent = this.getConsent();
    if (!consent) return null;

    try {
      const expiresAt = new Date(consent.expiresAt);
      const now = new Date();
      const diffMs = expiresAt.getTime() - now.getTime();
      const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
      return diffDays;
    } catch {
      return null;
    }
  }
}

// Export singleton instance
export const cookieConsentService = new CookieConsentService();
